import { getTenantId } from './constants';

// ── Thresholds ───────────────────────────────────────────────────────────────
// Used as fallback when the API's creditBalanceStatus field is unrecognised.
// deriveCreditState prefers the API status string; these kick in only for
// unknown/future status values.
export const RUNNING_LOW_THRESHOLD = 20; // % — warn below this
export const OUT_OF_CREDITS_THRESHOLD = 5; // % — exhausted below this

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * The five states Porter's credit UI can be in.
 * - `'loading'`   — initial / fetch in-flight (no data yet)
 * - `'healthy'`   — balance is fine, no warning needed
 * - `'low'`       — balance is running low (amber warning)
 * - `'exhausted'` — out of credits (red warning)
 * - `'error'`     — API call failed and no prior data available
 */
export type CreditState = 'loading' | 'healthy' | 'low' | 'exhausted' | 'error';

/**
 * Relevant wallet fields returned by the metering API.
 * Source: GET /api/v1/wallets → _embedded.items[0]
 */
export interface WalletData {
  remainingPercentage: number;
  /** Raw status string from the API: 'HEALTHY' | 'LOW' | 'EMPTY' | 'NEGATIVE' */
  creditBalanceStatus: string;
}

// ── API fetch ────────────────────────────────────────────────────────────────

const METERING_BASE = '/services/xplan-ai-metering';

/**
 * Fetches the current user's wallet from the Xplan AI Metering API.
 *
 * Endpoint: GET /services/xplan-ai-metering/api/v1/wallets?tenantId={tenantId}
 *
 * The Metering team deploys their service behind the same XService Proxy as
 * Porter, making it same-origin. The XPLAN session cookie is auto-attached by
 * the browser (no explicit auth header needed). tenantId is sent as a query
 * param (metering API contract).
 *
 * Returns null on any failure (network, non-2xx, malformed body) — callers
 * must handle null gracefully and never block Porter's use on a failed fetch.
 */
export async function fetchWallet(signal?: AbortSignal): Promise<WalletData | null> {
  try {
    const tenantId = await getTenantId();
    const url = `${METERING_BASE}/api/v1/wallets?tenantId=${encodeURIComponent(tenantId)}`;
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const item = data?._embedded?.items?.[0];
    if (!item) return null;
    // Validate both fields before returning — a malformed response (missing or
    // wrong-type fields) must produce null (graceful) rather than a 0-percentage
    // exhausted mis-classification or a toUpperCase() throw in deriveCreditState.
    const remainingPercentage = item.remainingPercentage;
    const creditBalanceStatus = item.creditBalanceStatus;
    if (typeof remainingPercentage !== 'number' || !Number.isFinite(remainingPercentage) || typeof creditBalanceStatus !== 'string') {
      return null;
    }
    return {
      remainingPercentage,
      creditBalanceStatus,
    };
  } catch {
    // Network error, abort, or malformed JSON — return null (graceful)
    return null;
  }
}

// ── State derivation ─────────────────────────────────────────────────────────

/**
 * Maps raw WalletData to a CreditState.
 *
 * API creditBalanceStatus values: 'HEALTHY' | 'LOW' | 'EMPTY' | 'NEGATIVE'
 * - HEALTHY  → 'healthy'
 * - LOW      → 'low'
 * - EMPTY    → 'exhausted' (out of credits)
 * - NEGATIVE → 'exhausted' (treat same as EMPTY in the UI)
 *
 * Falls back to percentage thresholds for any unrecognised status value.
 * Returns 'error' when data is null.
 */
export function deriveCreditState(data: WalletData | null): CreditState {
  if (data === null) return 'error';

  const status = data.creditBalanceStatus.toUpperCase();

  if (status === 'EMPTY' || status === 'NEGATIVE') return 'exhausted';
  if (status === 'LOW') return 'low';
  if (status === 'HEALTHY') return 'healthy';

  // Fallback: derive from percentage when status is unrecognised/empty string
  if (data.remainingPercentage <= OUT_OF_CREDITS_THRESHOLD) return 'exhausted';
  if (data.remainingPercentage <= RUNNING_LOW_THRESHOLD) return 'low';
  return 'healthy';
}
